import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { session } from '../auth/session';
import type { ApiEnvelope } from '../types/api';

export const http = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  timeout: 20_000, // uploads/downloads override this per request
  // No default Content-Type: axios sets JSON for objects and multipart (with boundary) for FormData.
});

http.interceptors.request.use((config) => {
  const token = session.getToken();
  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  return config;
});

let inflightRefresh: Promise<string> | null = null;

async function requestNewAccessToken(): Promise<string> {
  const res = await axios.post<ApiEnvelope<{ accessToken: string }>>('/api/v1/auth/refresh', null, { withCredentials: true, timeout: 10_000 });
  return res.data.data.accessToken;
}

/**
 * Single-flight refresh. The Web Locks API serialises refreshes across tabs: without it two tabs could
 * present the same rotating refresh token, which the server treats as token theft and revokes the session.
 */
export function refreshAccessToken(): Promise<string> {
  inflightRefresh ??= (async () => {
    try {
      const token =
        typeof navigator !== 'undefined' && 'locks' in navigator
          ? await navigator.locks.request('resolveone-refresh', requestNewAccessToken)
          : await requestNewAccessToken();
      session.set(token);
      return token;
    } catch (err) {
      session.clear();
      throw err;
    } finally {
      inflightRefresh = null;
    }
  })();
  return inflightRefresh;
}

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

http.interceptors.response.use(undefined, async (error: unknown) => {
  if (!(error instanceof AxiosError) || error.response?.status !== 401 || !error.config) throw error;
  const config = error.config as RetriableConfig;
  if (config._retried || config.url?.startsWith('/auth/')) throw error;
  config._retried = true;
  await refreshAccessToken();
  return http.request(config as AxiosRequestConfig);
});

export async function getData<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await http.get<ApiEnvelope<T>>(url, config);
  return res.data.data;
}

export async function sendData<T>(method: 'post' | 'patch', url: string, body?: unknown): Promise<T> {
  const res = await http.request<ApiEnvelope<T>>({ method, url, data: body });
  return res.data.data;
}
